import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import HomeLegacy from './pages/HomeLegacy';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import OrderTracking from './pages/OrderTracking';
import Login from './pages/Login';
import Register from './pages/Register';
import Account from './pages/Account';
import { cartAPI } from './services/api';
import { AuthContext } from './context/AuthContext';
import { CartContext } from './context/CartContext';
import './styles/globals.css';

function FloatingWhatsApp({ onClick }) {
  const location = useLocation();
  if (location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/product/')) return null;

  return (
    <button
      type="button"
      className="floating-whatsapp-btn"
      onClick={onClick}
      aria-label="Ask on WhatsApp"
    >
      <span className="floating-whatsapp-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M20.52 3.48A11.83 11.83 0 0 0 12.06 0C5.64 0 .51 5.13.51 11.55c0 2.03.53 4.01 1.54 5.76L.4 23.5l6.41-1.63A11.6 11.6 0 0 0 12.06 23c6.41 0 11.54-5.13 11.54-11.55 0-3.08-1.2-5.98-3.08-8.16ZM12.06 21.1c-1.83 0-3.66-.5-5.23-1.44l-.38-.22-3.8.97 1.01-3.7-.25-.38A9.44 9.44 0 0 1 2.5 11.55c0-5.2 4.36-9.42 9.56-9.42 2.53 0 4.92 1 6.71 2.79a9.3 9.3 0 0 1 2.79 6.63c0 5.2-4.25 9.55-9.5 9.55Zm5.22-7.08c-.28-.15-1.67-.82-1.93-.92-.26-.1-.45-.15-.64.15-.2.28-.77.92-.94 1.11-.17.2-.35.22-.63.08-.28-.15-1.19-.43-2.26-1.4-.83-.74-1.4-1.64-1.55-1.92-.17-.28-.02-.43.12-.57.13-.13.28-.35.42-.52.14-.17.18-.3.28-.5.1-.2.05-.38-.03-.53-.08-.15-.64-1.54-.88-2.11-.23-.56-.46-.48-.64-.49l-.55-.01c-.19 0-.5.07-.76.35-.26.28-1 .98-1 2.39 0 1.4 1.03 2.77 1.17 2.96.14.2 2.03 3.1 4.93 4.35.69.29 1.23.47 1.65.59.7.22 1.34.19 1.84.12.56-.08 1.67-.68 1.9-1.34.23-.66.23-1.23.16-1.35-.08-.12-.28-.18-.57-.33Z" fill="currentColor"/>
        </svg>
      </span>
      <span>Ask on WhatsApp</span>
    </button>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    
    setLoading(false);
  }, []);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const login = (token, userData, refreshToken) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('token', token);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    try {
      const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
      const cartOwnerId = localStorage.getItem('cart-owner-id');
      const userId = userData?._id || userData?.id;
      if (savedCart.length > 0 && !cartOwnerId) localStorage.setItem('guest-cart-pending', 'true');
      if (cartOwnerId && userId && cartOwnerId !== userId) localStorage.removeItem('guest-cart-pending');
    } catch (error) {
      localStorage.removeItem('guest-cart-pending');
    }
    setUser(userData);
  };

  const register = login;

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const askOnWhatsApp = () => {
    const message = window.location.pathname.startsWith('/product/')
      ? `Hi, I am interested in this product:\n${window.location.href}`
      : `Hi, I would like to know more about Starlight Threading outfits.\n${window.location.href}`;
    window.open(`https://wa.me/918309158686?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const logout = async () => {
    try {
      if (localStorage.getItem('access_token') || localStorage.getItem('token')) {
        await import('./services/api').then(({ authAPI }) => authAPI.logout());
      }
    } catch (error) {
      // Local logout still completes if the network is unavailable.
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const normalizeCart = (serverCart) => (serverCart?.items || []).map((item) => ({
    ...item.productId,
    _id: item.productId?._id || item.productId,
    cartItemId: item._id,
    quantity: item.quantity,
    size: item.size,
    color: item.color,
    ageGroup: item.ageGroup,
    cartImage: item.imageUrl || item.cartImage || '',
    price: item.price ?? item.productId?.price
  }));

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const syncGuestCart = async () => {
      let guestCart = [];
      try {
        const savedCart = localStorage.getItem('cart');
        guestCart = savedCart ? JSON.parse(savedCart) : [];
      } catch (error) {
        guestCart = [];
      }

      try {
        const response = await cartAPI.getCart();
        const shouldMigrateGuestCart = localStorage.getItem('guest-cart-pending') === 'true';
        if (shouldMigrateGuestCart && guestCart.length > 0) {
          await Promise.all(guestCart.map((item) => cartAPI.addToCart({
            productId: item._id,
            quantity: Number(item.quantity || 1),
            size: item.size,
            color: item.color,
            ageGroup: item.ageGroup || '1-3',
            imageUrl: item.cartImage || item.image || item.images?.[0]?.url || '',
            price: Number(item.price || 0)
          })));
          const mergedResponse = await cartAPI.getCart();
          localStorage.removeItem('guest-cart-pending');
          localStorage.setItem('cart-owner-id', user?._id || user?.id || '');
          if (!cancelled) setCart(normalizeCart(mergedResponse.data.cart));
        } else if (!cancelled) {
          localStorage.setItem('cart-owner-id', user?._id || user?.id || '');
          setCart(normalizeCart(response.data.cart));
        }
      } catch (error) {
        if (!cancelled && guestCart.length > 0) setCart(guestCart);
      }
    };

    syncGuestCart();
    return () => { cancelled = true; };
  }, [user]);

  const addToCart = async (product) => {
    if (user) {
      try {
        const response = await cartAPI.addToCart({
          productId: product._id,
          quantity: product.quantity || 1,
          size: product.size,
          color: product.color,
          ageGroup: product.ageGroup || '1-3',
          imageUrl: product.cartImage || product.image || product.images?.[0]?.url || '',
          price: product.price
        });
        setCart(normalizeCart(response.data.cart));
        return;
      } catch (error) {
        console.error('Unable to sync cart:', error);
      }
    }
    const existingItem = cart.find(item => 
      item._id === product._id && 
      item.size === product.size && 
      item.color === product.color &&
      item.ageGroup === product.ageGroup
    );

    if (existingItem) {
      setCart(cart.map(item =>
        item._id === product._id && 
        item.size === product.size && 
        item.color === product.color &&
        item.ageGroup === product.ageGroup
          ? {
            ...item,
            quantity: item.quantity + (product.quantity || 1),
            price: product.price,
            cartImage: product.cartImage || item.cartImage
          }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: product.quantity || 1, ageGroup: product.ageGroup || '1-3' }]);
    }
  };

  const updateCartItem = async (item, quantity) => {
    if (user && item.cartItemId) {
      const response = await cartAPI.updateCartItem(item.cartItemId, { quantity });
      setCart(normalizeCart(response.data.cart));
      return;
    }
    setCart((currentCart) => currentCart.map((currentItem) =>
      currentItem === item
        ? { ...currentItem, quantity }
        : currentItem
    ));
  };

  const removeCartItem = async (item) => {
    const previousCart = cart;
    const matchesItem = (currentItem) => item.cartItemId
      ? currentItem.cartItemId === item.cartItemId
      : currentItem === item;
    const nextCart = cart.filter((currentItem) => !matchesItem(currentItem));

    setCart(nextCart);
    localStorage.setItem('cart', JSON.stringify(nextCart));

    if (!user) return;

    try {
      if (item.cartItemId) {
        await cartAPI.removeFromCart(item.cartItemId);
      } else if (previousCart.length === 1) {
        await cartAPI.clearCart();
      }
    } catch (error) {
      setCart(previousCart);
      localStorage.setItem('cart', JSON.stringify(previousCart));
      console.error('Unable to remove cart item:', error);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item._id !== productId));
  };

  const clearCart = async () => {
    if (user) {
      try {
        await cartAPI.clearCart();
      } catch (error) {
        console.error('Unable to clear server cart:', error);
      }
    }
    setCart([]);
    localStorage.setItem('cart', JSON.stringify([]));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <AuthContext.Provider value={{ user, login, register, logout, updateUser }}>
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateCartItem, removeCartItem, clearCart, setCart }}>
          <div className="App">
            <Navbar />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/classic-home" element={<HomeLegacy />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/order-tracking/:orderId" element={<OrderTracking />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/account" element={<Account />} />
              </Routes>
            </main>
            <FloatingWhatsApp onClick={askOnWhatsApp} />
            <Footer />
          </div>
        </CartContext.Provider>
      </AuthContext.Provider>
    </Router>
  );
}

export default App;
